import React, { useMemo, ReactNode, isValidElement, cloneElement } from "react";

export const EMOJI_REGEX =
  /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\p{Extended_Pictographic})/gu;

export const useIsOnlyEmoji = (text: string): boolean => {
  return useMemo(() => {
    if (!text || typeof text !== "string") return false;
    const stripped = text.replace(EMOJI_REGEX, "").trim();
    return stripped === "" && text.trim() !== "";
  }, [text]);
};

export const useEmojiWrapper = (
  text: string,
  emojiClassName: string = "cbb-msg-emoji-inline",
  customStyles?: React.CSSProperties,
) => {
  return useMemo(() => {
    if (!text || typeof text !== "string") return text;

    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    const matches = [...text.matchAll(EMOJI_REGEX)];

    matches.forEach((match, idx) => {
      if (match.index !== undefined && match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      const defaultStyles: React.CSSProperties = {
        display: "inline-block",
        background: "none",
        backgroundClip: "initial",
        WebkitBackgroundClip: "initial",
        WebkitTextFillColor: "initial",
        color: "inherit",
      };

      parts.push(
        <span
          key={`emoji-${idx}`}
          className={emojiClassName}
          style={{ ...defaultStyles, ...customStyles }}
        >
          {match[0]}
        </span>,
      );

      if (match.index !== undefined) {
        lastIndex = match.index + match[0].length;
      }
    });

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  }, [text, emojiClassName, customStyles]);
};

export const isOnlyEmoji = (text: string): boolean => {
  if (!text || typeof text !== "string") return false;
  const stripped = text.replace(EMOJI_REGEX, "").trim();
  return stripped === "" && text.trim() !== "";
};

export const processTextWithEmojis = (
  text: string,
  emojiClassName: string = "cbb-msg-emoji-inline",
  customStyles?: React.CSSProperties,
): (string | JSX.Element)[] | string => {
  if (!text || typeof text !== "string") return text;

  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  const matches = [...text.matchAll(EMOJI_REGEX)];

  matches.forEach((match, idx) => {
    if (match.index !== undefined && match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const defaultStyles: React.CSSProperties = {
      display: "inline-block",
      background: "none",
      backgroundClip: "initial",
      WebkitBackgroundClip: "initial",
      WebkitTextFillColor: "initial",
      color: "inherit",
    };

    parts.push(
      <span
        key={`emoji-${idx}`}
        className={emojiClassName}
        style={{ ...defaultStyles, ...customStyles }}
      >
        {match[0]}
      </span>,
    );

    if (match.index !== undefined) {
      lastIndex = match.index + match[0].length;
    }
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

export const hasEmojis = (text: string): boolean => {
  const regex = new RegExp(EMOJI_REGEX.source, EMOJI_REGEX.flags);
  return regex.test(text);
};

export const extractEmojis = (text: string): string[] => {
  return text.match(EMOJI_REGEX) || [];
};

export const wrapEmojisInHTML = (
  text: string,
  className: string = "cbb-msg-emoji-inline",
): string => {
  if (!text) return text;
  const regex = new RegExp(EMOJI_REGEX.source, EMOJI_REGEX.flags);
  return text.replace(
    regex,
    (emoji) =>
      `<span class="${className}" style="display:inline-block !important;background:none !important;background-clip:initial !important;-webkit-background-clip:initial !important;-webkit-text-fill-color:initial !important;color:inherit !important;">${emoji}</span>`,
  );
};

export const processChildrenWithEmojis = (
  children: ReactNode,
  emojiClassName: string = "cbb-msg-emoji-inline",
  customStyles?: React.CSSProperties,
): ReactNode => {
  if (typeof children === "string") {
    return processTextWithEmojis(children, emojiClassName, customStyles);
  }

  if (Array.isArray(children)) {
    return React.Children.map(children, (child) =>
      processChildrenWithEmojis(child, emojiClassName, customStyles),
    );
  }

  if (isValidElement(children)) {
    return cloneElement(
      children as React.ReactElement<any>,
      (children as React.ReactElement<any>).props,
      processChildrenWithEmojis(
        (children as React.ReactElement<any>).props.children,
        emojiClassName,
        customStyles,
      ),
    );
  }

  return children;
};

export default useEmojiWrapper;
